import { Form, Input, Col, Select } from 'antd'
import { Translate } from 'lang'
import toLower from 'lodash/toLower'
import { DDIList } from '../assets/DDI'

const style = {
  form: {
    display: 'inline-block',
    width: '100%',
  },
}

export function formatPhoneNumber(value = '') {
  const digits = value.replace(/\D/g, '').slice(0, 11)

  if (!digits) return ''
  if (digits.length <= 2) return `(${digits}`

  let formatted = `(${digits.slice(0, 2)})`

  if (digits.length > 2) {
    formatted += ` ${digits.slice(2, 3)}`
  }
  if (digits.length > 3) {
    formatted += `.${digits.slice(3, 7)}`
  }
  if (digits.length > 7) {
    formatted += `.${digits.slice(7, 11)}`
  }

  return formatted
}

const onlyDigits = value => `${value ?? ''}`.replace(/\D/g, '')

const isBrazilianCountryCode = value => onlyDigits(value) === '55'

export function hasPhoneInitialValue(initialValues = {}) {
  return Boolean(
    initialValues?.phone ||
      initialValues?.full_number_phone ||
      initialValues?.country_code ||
      initialValues?.area_code ||
      initialValues?.phone_number,
  )
}

export function buildPhonePayload(phone = {}) {
  const countryCodeRaw = `${phone?.ddi ?? ''}`.trim()
  const countryCode = onlyDigits(countryCodeRaw)
  const numberDigits = onlyDigits(phone?.number).slice(0, 11)
  const areaCode = numberDigits.slice(0, 2)
  const phoneNumberWithNinth = numberDigits.slice(2)
  const phoneNumber =
    countryCode === '55' && phoneNumberWithNinth.length === 9 && phoneNumberWithNinth.startsWith('9')
      ? phoneNumberWithNinth.slice(1)
      : phoneNumberWithNinth
  const fullNationalNumber = `${areaCode}${phoneNumberWithNinth}`

  return {
    country_code: countryCode,
    area_code: areaCode,
    phone_number: phoneNumber,
    full_number_phone: countryCodeRaw ? `${countryCodeRaw} ${fullNationalNumber}` : fullNationalNumber,
  }
}

export function getPhoneInitialValue(initialValues = {}, defaultCountryCode = '+55') {
  const rawPhone = `${initialValues?.full_number_phone ?? initialValues?.phone ?? ''}`.trim()
  if (rawPhone) {
    const [firstToken = '', ...restTokens] = rawPhone.split(/\s+/)
    const hasCountryCode = firstToken.startsWith('+')
    const numberPart = hasCountryCode ? restTokens.join(' ') : rawPhone
    const ddi = hasCountryCode ? firstToken : defaultCountryCode

    return {
      ddi,
      number: isBrazilianCountryCode(ddi) ? formatPhoneNumber(numberPart) : numberPart,
    }
  }

  if (initialValues?.phone && typeof initialValues.phone === 'object') {
    const ddi = initialValues.phone.ddi ?? defaultCountryCode

    return {
      ddi,
      number: isBrazilianCountryCode(ddi)
        ? formatPhoneNumber(initialValues.phone.number)
        : initialValues.phone.number,
    }
  }

  if (initialValues?.country_code || initialValues?.area_code || initialValues?.phone_number) {
    const countryDigits = onlyDigits(initialValues.country_code)
    const rawPhoneNumber = onlyDigits(initialValues.phone_number)
    const phoneNumber = countryDigits === '55' && rawPhoneNumber.length === 8 ? `9${rawPhoneNumber}` : rawPhoneNumber
    const numberDigits = `${initialValues.area_code ?? ''}${phoneNumber}`
    const ddi = initialValues.country_code
      ? `+${onlyDigits(initialValues.country_code)}`
      : defaultCountryCode

    return {
      ddi,
      number: isBrazilianCountryCode(ddi) ? formatPhoneNumber(numberDigits) : numberDigits,
    }
  }
  return { ddi: defaultCountryCode, number: '' }
}

export function normalizePhoneInitialValue(initialValues = {}, defaultCountryCode = '+55') {
  if (!hasPhoneInitialValue(initialValues)) {
    return initialValues
  }

  return {
    ...initialValues,
    phone: getPhoneInitialValue(initialValues, defaultCountryCode),
  }
}

export function applyPhonePayload(values = {}) {
  const phonePayload = buildPhonePayload(values.phone)
  const formValues = { ...values }

  delete formValues.phone

  return {
    ...formValues,
    ...phonePayload,
  }
}

const prefixSelector = (name, disabled) => (
  <Form.Item name={[name, 'ddi']} noStyle>
    <Select
      showSearch
      filterOption={(input, option) =>
        option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
      }
      style={{ minWidth: 100, maxWidth: 200 }}
      disabled={disabled}
      tabIndex={-1}
    >
      {DDIList.map(option => (
        <Select.Option key={`key-${option.value}`} value={option.value}>
          {option.label}
        </Select.Option>
      ))}
    </Select>
  </Form.Item>
)

export const PhoneForm = ({ value, allValues }) => {
  const { name, label, disabled, rules = [], required, placeholder, cols = { xs: 24 } } = value
  const ddi = allValues?.[name]?.ddi
  const isBrazilian = isBrazilianCountryCode(ddi)
  const defaultPlaceholder = Translate({
    messageKey: 'inform',
    gender: 'male',
    items: {
      item: toLower(label),
    },
  })
  return (
    <Col {...cols}>
      <Form.Item
        name={[name, 'number']}
        label={label}
        getValueFromEvent={event =>
          isBrazilian ? formatPhoneNumber(event?.target?.value) : event?.target?.value
        }
        rules={[
          { required },
          ...(isBrazilian
            ? [
                {
                  pattern: /^\(\d{2}\)\s\d\.\d{4}\.\d{4}$/,
                  message: 'Informe o telefone no formato (xx) x.xxxx.xxxx',
                },
              ]
            : []),
          ...rules,
        ]}
        style={style.form}
      >
        <Input
          type="text"
          inputMode={isBrazilian ? 'numeric' : undefined}
          maxLength={isBrazilian ? 16 : undefined}
          addonBefore={prefixSelector(name, disabled)}
          placeholder={placeholder ?? defaultPlaceholder}
          disabled={disabled}
        />
      </Form.Item>
    </Col>
  )
}
